<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Additional fiscal reference"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasAdditionalFiscalReferenceData"
            :has-errors="hasSectionErrors"
          />
          Additional fiscal reference
        </span>
      </template>
      <div
        v-for="(reference, referenceIndex) in parcel.additional_fiscal_references"
        :key="referenceIndex"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Role"
              :rules="rowHasData(reference) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Role <span class="text-danger">*</span>
                  <small class="text-muted">1316031000</small>
                </label>
                <b-form-input
                  v-model="reference.role"
                  v-b-tooltip.hover.top="getIssueMessage(`additional_fiscal_references.${referenceIndex}.role`)"
                  :class="getIssueClass(`additional_fiscal_references.${referenceIndex}.role`)"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="VAT identification number"
              :rules="rowHasData(reference) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  VAT identification number <span class="text-danger">*</span>
                  <small class="text-muted">1316034000</small>
                </label>
                <b-form-input
                  v-model="reference.vat_identification_number"
                  v-b-tooltip.hover.top="getIssueMessage(`additional_fiscal_references.${referenceIndex}.vat_identification_number`)"
                  :class="getIssueClass(`additional_fiscal_references.${referenceIndex}.vat_identification_number`)"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          />

          <b-col
            cols="12"
            md="1"
            class="text-right"
          >
            <b-button
              v-if="!disabled"
              variant="link"
              size="sm"
              class="text-danger mt-2"
              @click="removeReference(referenceIndex)"
            >
              <feather-icon icon="TrashIcon" />
              Delete
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col class="text-right">
          <b-button
            v-if="!disabled"
            variant="info"
            size="sm"
            @click="addReference"
          >
            <feather-icon icon="PlusIcon" />
            New taxable person
          </b-button>
        </b-col>
      </b-row>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasAdditionalFiscalReferenceData() {
      if (!this.parcel || !this.parcel.additional_fiscal_references || !Array.isArray(this.parcel.additional_fiscal_references)) {
        return false
      }
      return this.parcel.additional_fiscal_references.some(ref => ref.role || ref.vat_identification_number)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('additional_fiscal_references.'))
    },
  },
  methods: {
    addReference() {
      if (!this.parcel.additional_fiscal_references) {
        this.$set(this.parcel, 'additional_fiscal_references', [])
      }
      this.parcel.additional_fiscal_references.push({
        role: '',
        vat_identification_number: '',
      })
    },
    removeReference(referenceIndex) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this fiscal reference?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      }).then(value => {
        if (value) {
          this.parcel.additional_fiscal_references.splice(referenceIndex, 1)
        }
      })
    },
    rowHasData(reference) {
      return !!(reference.role || reference.vat_identification_number)
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
